import type { FeatureHit } from '../map/featureInfo'
import { displayNameText } from '../map/layerNames'
import { attributeLabel } from '../map/layerAttributes'
import type { RightPanel } from './rightPanel'
import { escapeHtml } from './dom'

/**
 * "Details" tab of the right panel (T2, re-homed in T5): GetFeatureInfo
 * results — attribute tables grouped by layer, plus the Loading/empty/error
 * messages. It no longer owns the right zone; `rightPanel` hands it a pane.
 *
 * Every reveal goes through `present()`, never `open()`: a map click must not
 * pull the user off the Data Filter form they are filling in.
 */
export interface DetailsPanel {
  showLoading(): void
  showHits(hits: FeatureHit[]): void
  showError(): void
  hide(): void
}

export function createDetailsPanel(right: RightPanel): DetailsPanel {
  const pane = right.addTab('details', 'Details')
  const content = document.createElement('div')
  content.className = 'details-panel__content'
  pane.appendChild(content)

  const reveal = (html: string): void => {
    content.innerHTML = html
    right.present('details')
  }

  return {
    showLoading() {
      reveal(`<p class="details-panel__muted">Loading…</p>`)
    },
    showHits(hits) {
      if (hits.length === 0) {
        reveal(`<p class="details-panel__muted">No feature at this location.</p>`)
        return
      }
      reveal(
        hits
          .map((hit) => {
            const rows = Object.entries(hit.properties)
              .filter(([, value]) => value !== null && value !== undefined && value !== '')
              .map(
                ([key, value]) =>
                  // Readable label, raw column name as the tooltip — same
                  // convention as the Attributes table header.
                  `<tr><th title="${escapeHtml(key)}">${escapeHtml(
                    attributeLabel(hit.layerName, hit.layerTitle, key),
                  )}</th><td>${escapeHtml(String(value))}</td></tr>`,
              )
              .join('')
            const body = rows
              ? `<table class="details-table"><tbody>${rows}</tbody></table>`
              : `<p class="details-panel__muted">No details to show for this layer.</p>`
            return `
        <div class="details-panel__group">
          <h4>${escapeHtml(displayNameText(hit.layerName, hit.layerTitle))}</h4>
          ${body}
        </div>`
          })
          .join(''),
      )
    },
    showError() {
      reveal(`<p class="details-panel__muted">Could not load feature info.</p>`)
    },
    hide() {
      right.close()
    },
  }
}
